/**
 * System-wide metrics calculator
 * Aggregates metrics from all node types into system-level totals
 */
import { convertCapacity, convertThroughput } from '../../utils/configUtilities';
import type { SystemConfiguration } from '../../domain';
import type { MetricsContext, ProductDb, MetricsDb, NodeMetricsBag } from '../types';

/**
 * Calculate system-wide aggregate metrics
 * @param system - SystemConfiguration design box
 * @param ctx - Cross-box inputs (isPBUnit)
 * @param productDb - Product database
 * @param metricsDb - Metrics database
 * @param nodeMetrics - Collected metrics from all node calculators
 * @returns System-level aggregate metrics
 */
export default function calculateSystemMetrics(
  system: SystemConfiguration,
  ctx: MetricsContext,
  productDb: ProductDb,
  metricsDb: MetricsDb,
  nodeMetrics: NodeMetricsBag,
) {
  const {
    directorMetrics = {},
    hybridMetrics = {},
    afaMetrics = {}
  } = nodeMetrics;

  // ========== CAPACITY AGGREGATIONS ==========
  // MORK TODO : Calculations need to account for the fact that directors may no longer be included (see Expansion)

  // Total SSD capacity across all nodes
  const totalSsdCapacity =
    (system.director.veloCount > 0 ? directorMetrics.totalVeloSsdCapacity : 0) +
    (system.hybrid.enabled ? hybridMetrics.totalHybridSsdCapacity : 0) +
    (system.afa.enabled ? afaMetrics.totalAfaSsdCapacity : 0);

  // Total raw capacity (SSD + HDD + Director SSD)
  const totalRawCapacity =
    (system.director.veloCount > 0 ? directorMetrics.totalVeloSsdCapacity : 0) +
    (system.hybrid.enabled ? hybridMetrics.totalHybridRawCapacity : 0) +
    (system.afa.enabled ? afaMetrics.totalAfaRawCapacity : 0);

  // Total effective capacity
  const totalEffectiveCapacity =
    (system.hybrid.enabled ? hybridMetrics.hybridEffectiveCapacity : 0) +
    (system.afa.enabled ? afaMetrics.afaEffectiveCapacity : 0);

  // Total compressed effective capacity
  const totalCompressedEffectiveCapacity =
    (system.hybrid.enabled ? hybridMetrics.hybridCompressedEffectiveCapacity : 0) +
    (system.afa.enabled ? afaMetrics.afaCompressedEffectiveCapacity : 0);

  // ========== PERFORMANCE AGGREGATIONS ==========

  // Total IOPS
  const totalIops =
    (system.hybrid.enabled ? hybridMetrics.hybridIops : 0) +
    (system.afa.enabled ? afaMetrics.afaIops : 0);

  // Total throughput (read/write) - currently these metrics aren't really used system-wide. Don't see a usecase where combined NVMe throughput is needed.
  const totalNvmeReadTransferRate =
    (hybridMetrics.hybridNvmeReadTransferRate || 0) +
    (afaMetrics.afaReadTransferRate || 0);

  const totalNvmeWriteTransferRate =
    (hybridMetrics.hybridNvmeWriteTransferRate || 0) +
    (afaMetrics.afaWriteTransferRate || 0);

  const totalJbodReadTransferRate =
    (hybridMetrics.hybridJbodReadTransferRate || 0);

  const totalJbodWriteTransferRate =
    (hybridMetrics.hybridJbodWriteTransferRate || 0);

  // Inodes calculation (directors + storage nodes)
  const totalVpodCountForInodes =
    (system.hybrid.enabled ? system.hybrid.nodeCount * metricsDb.global.vpodsPerNode : 0) +
    (system.afa.enabled ? system.afa.nodeCount * metricsDb.global.vpodsPerNode : 0);
  const totalInodes = totalVpodCountForInodes * metricsDb.global.inodesPerVpod;

  // ========== PHYSICAL AGGREGATIONS ==========

  // Total power consumption
  const totalNominalPower =
    (directorMetrics.veloNominalPower || 0) +
    (hybridMetrics.hybridTotalNominalPower || 0) +
    (afaMetrics.afaVpodNominalPower || 0);

  const totalMaxPower =
    (directorMetrics.veloMaxPower || 0) +
    (hybridMetrics.hybridTotalMaxPower || 0) +
    (afaMetrics.afaVpodMaxPower || 0);

  // Total weight
  const totalWeight =
    (directorMetrics.veloWeight || 0) +
    (hybridMetrics.hybridTotalWeight || 0) +
    (afaMetrics.afaVpodWeight || 0);

  // Total rack units
  const totalRU =
    (directorMetrics.veloRU || 0) +
    (hybridMetrics.hybridTotalRU || 0) +
    (afaMetrics.afaVpodRU || 0);

  // ========== EFFICIENCY RATIOS ==========

  // Ratio of SSD to total raw capacity
  const ratioTotalSsdHdd = totalRawCapacity > 0
    ? (totalSsdCapacity / totalRawCapacity) * 100
    : 0;

  // Ratio of hybrid SSD to hybrid raw capacity
  const ratioHybridSsdHdd = hybridMetrics.totalHybridRawCapacity > 0
    ? (hybridMetrics.totalHybridSsdCapacity / hybridMetrics.totalHybridRawCapacity) * 100
    : 0;

  // Useable efficiency (effective / raw)
  const useableEff = totalRawCapacity > 0
    ? (totalEffectiveCapacity / totalRawCapacity) * 100
    : 0;

  // Effective efficiency (with compression)
  const effectiveEff = totalRawCapacity > 0
    ? (totalCompressedEffectiveCapacity / totalRawCapacity) * 100
    : 0;

  // ========== RETURN ALL SYSTEM METRICS ==========
  // MORK TODO : Eventually phase out all backwards compatible variables after front-end UI refactor to use system level metrics only.
  return {
    // ============ CANONICAL CAPACITY (decimal TB) — DO NOT CONVERT ============
    // CONF-136: The PB/PiB toggle (config.isPBUnit) is a DISPLAY preference only.
    // The converted fields below (totalSsdCapacity, totalRawCapacity, ...) are
    // multiplied by ~0.9095 in binary (PiB) mode and are correct for UI/PDF/PPT
    // display ONLY. They must never feed unit math, because
    // Math.ceil(capacity / 10) would then return a different VDURA Care
    // subscription-unit count per toggle state — changing the price for an
    // unchanged configuration (CONF-136).
    //
    // These `*TB` siblings carry the raw decimal-TB value and are toggle-invariant.
    // ALL pricing, BOM, and drive-count math MUST read the `*TB` fields, never the
    // converted ones. Two parallel fields is a deliberate trade-off: it isolates the
    // ~50 display read sites (zero regression risk) at the cost of a naming rule.
    // Guarded by src/metrics/capacityUnitInvariance.test.js — keep it green.
    totalSsdCapacityTB: totalSsdCapacity,
    totalHddCapacityTB: hybridMetrics.totalHddCapacity || 0,
    totalRawCapacityTB: totalRawCapacity,
    totalHybridSsdCapacityTB: hybridMetrics.totalHybridSsdCapacity || 0,
    totalAfaSsdCapacityTB: afaMetrics.totalAfaSsdCapacity || 0,
    totalAfaRawCapacityTB: afaMetrics.totalAfaRawCapacity || 0,

    // ===================== Capacity aggregates =====================
    // Convert all capacity values from TB to TB/TiB based on config
    totalSsdCapacity: convertCapacity(totalSsdCapacity, ctx.isPBUnit),
    totalHddCapacity: convertCapacity(hybridMetrics.totalHddCapacity || 0, ctx.isPBUnit),
    totalRawCapacity: convertCapacity(totalRawCapacity, ctx.isPBUnit),
    totalEffectiveCapacity: convertCapacity(totalEffectiveCapacity, ctx.isPBUnit),
    totalCompressedEffectiveCapacity: convertCapacity(totalCompressedEffectiveCapacity, ctx.isPBUnit),

    // Hybrid-specific (for backward compatibility) - note these come from hybridMetrics which are in TB
    totalHybridRawCapacity: convertCapacity(hybridMetrics.totalHybridRawCapacity || 0, ctx.isPBUnit),
    totalHybridSsdCapacity: convertCapacity(hybridMetrics.totalHybridSsdCapacity || 0, ctx.isPBUnit),
    hybridEffectiveCapacity: convertCapacity(hybridMetrics.hybridEffectiveCapacity || 0, ctx.isPBUnit),
    hybridCompressedEffectiveCapacity: convertCapacity(hybridMetrics.hybridCompressedEffectiveCapacity || 0, ctx.isPBUnit),
    hybridBurstCapacity: convertCapacity(hybridMetrics.hybridBurstCapacity || 0, ctx.isPBUnit),

    // AFA-specific (for backward compatibility)
    totalAfaSsdCapacity: convertCapacity(afaMetrics.totalAfaSsdCapacity || 0, ctx.isPBUnit),
    totalAfaRawCapacity: convertCapacity(afaMetrics.totalAfaRawCapacity || 0, ctx.isPBUnit),
    afaEffectiveCapacity: convertCapacity(afaMetrics.afaEffectiveCapacity || 0, ctx.isPBUnit),
    afaCompressedEffectiveCapacity: convertCapacity(afaMetrics.afaCompressedEffectiveCapacity || 0, ctx.isPBUnit),

    // ===================== Performance aggregates =====================
    // IOPS and Metadata - NO CONVERSION (just counts)
    totalIops,
    totalMetadata: directorMetrics.totalMetadata || 0,
    totalInodes,

    // Throughput - Convert all from GB/s to GB/s or GiB/s
    totalNvmeReadTransferRate: convertThroughput(totalNvmeReadTransferRate, ctx.isPBUnit),
    totalNvmeWriteTransferRate: convertThroughput(totalNvmeWriteTransferRate, ctx.isPBUnit),
    totalJbodReadTransferRate: convertThroughput(totalJbodReadTransferRate, ctx.isPBUnit),
    totalJbodWriteTransferRate: convertThroughput(totalJbodWriteTransferRate, ctx.isPBUnit),

    // Hybrid performance (for backward compatibility)
    hybridIops: hybridMetrics.hybridIops || 0,        // NO CONVERSION
    hybridReadTransferRate: convertThroughput(hybridMetrics.hybridJbodReadTransferRate || 0, ctx.isPBUnit),
    hybridWriteTransferRate: convertThroughput(hybridMetrics.hybridJbodWriteTransferRate || 0, ctx.isPBUnit),
    hybridBurstReadTransferRate: convertThroughput(hybridMetrics.hybridNvmeReadTransferRate || 0, ctx.isPBUnit),
    hybridBurstWriteTransferRate: convertThroughput(hybridMetrics.hybridNvmeWriteTransferRate || 0, ctx.isPBUnit),

    // AFA performance (for backward compatibility)
    afaIops: afaMetrics.afaIops || 0,                 // NO CONVERSION
    afaReadTransferRate: convertThroughput(afaMetrics.afaReadTransferRate || 0, ctx.isPBUnit),
    afaWriteTransferRate: convertThroughput(afaMetrics.afaWriteTransferRate || 0, ctx.isPBUnit),
    // afaBurstReadTransferRate: afaMetrics.afaBurstReadTransferRate || 0,
    // afaBurstWriteTransferRate: afaMetrics.afaBurstWriteTransferRate || 0,

    // ===================== Reliability =====================
    // NO CONVERSION - these are "nines" (percentages)
    hybridDurabilityNines: hybridMetrics.hybridDurabilityNines || 0,
    hybridAvailabilityNines: hybridMetrics.hybridAvailabilityNines || 0,
    afaDurabilityNines: afaMetrics.afaDurabilityNines || 0,
    afaAvailabilityNines: afaMetrics.afaAvailabilityNines || 0,

    // ===================== Physical aggregates =====================
    // NO CONVERSION - watts, lbs, RU
    totalNominalPower,
    totalMaxPower,
    totalWeight,
    totalRU,

    // ===================== Efficiency ratios =====================
    // NO CONVERSION - these are percentages
    ratioTotalSsdHdd,
    ratioHybridSsdHdd,
    useableEff,
    effectiveEff
  };
}
